import { FC } from 'react';
import { CodeDisplayBox, CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Divider, Modal, Stack, Typography } from '@mui/material';
import { ModalHeader } from '~/components';
import { InfoIcon, TerminalIcon } from '~/icons';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { modalContainerStyle } from '../styles';

export interface DatasetConnectionInstructionsProps {
  payload: {
    id: string;
    roleArn: string;
    bucketName: string;
    bucketFolder?: string;
  };
}

const getBoto3Code = (
  roleArn: string,
  bucketName: string,
  bucketFolder: string,
  sessionName = 'AssumeStageProducerRoleSession',
) =>
  `import boto3\n\nsts_client = boto3.client("sts")\n\nassumed_role = sts_client.assume_role(\n    RoleArn="${roleArn}",\n    RoleSessionName="${sessionName}"\n)\n\ncreds = assumed_role["Credentials"]\n\ns3 = boto3.client(\n    "s3",\n    aws_access_key_id=creds["AccessKeyId"],\n    aws_secret_access_key=creds["SecretAccessKey"],\n    aws_session_token=creds["SessionToken"],\n)\nresp = s3.list_objects_v2(\n    Bucket="${bucketName}",\n    Prefix="${bucketFolder ? `${bucketFolder}/` : ''}",\n    Delimiter="/"\n)`;

const DatasetConnectionInstructions: FC<DatasetConnectionInstructionsProps> = ({
  payload: { id, roleArn, bucketName, bucketFolder = '' },
}) => {
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  return (
    <Modal open onClose={handleClose} disableAutoFocus>
      <Stack sx={modalContainerStyle}>
        <ModalHeader title={['Dataset Connection Information']} onClose={handleClose} />
        <Stack p={2} sx={{ overflowY: 'auto' }}>
          <Stack p={2} spacing={2}>
            <Typography variant="buttonTextStandard">
              Use the Python code below to access this dataset from a Jupyter notebook or Python script. The role ARN
              and S3 bucket are also provided if you need them for manual configuration or other tools.
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <TerminalIcon style={{ width: 36, height: 36 }} />
                <Stack>
                  <Typography variant="buttonTextStandard">Python code</Typography>
                  <Typography>
                    Copy the snippet below into a Jupyter notebook or Python script to assume the dataset role and start
                    working with the S3 bucket directly.
                  </Typography>
                </Stack>
              </Stack>
              <CodeDisplayBox darkMode code={getBoto3Code(roleArn, bucketName, bucketFolder)} />
            </Stack>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <InfoIcon style={{ width: 36, height: 36 }} />
                <Stack>
                  <Typography variant="buttonTextStandard">Connection details</Typography>
                  <Typography>
                    Use the role ARN and bucket name below if you need to configure access manually or use a different
                    tool.
                  </Typography>
                </Stack>
              </Stack>
              <Stack spacing={1}>
                <Typography variant="bodyDefaultStrong">Role ARN</Typography>
                <CodeDisplayBox darkMode code={roleArn} />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="bodyDefaultStrong">S3 bucket</Typography>
                <CodeDisplayBox darkMode code={`s3://${bucketName}${bucketFolder ? `/${bucketFolder}` : ''}`} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="end" p={2}>
          <CustomButton onClick={handleClose}>Ok</CustomButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default DatasetConnectionInstructions;
